import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AdminAlert } from "../components/AdminAlert";
import { AppForm, EMPTY_FORM, type FormState } from "../components/AppForm";
import { DeleteAppDialog } from "../components/DeleteAppDialog";
import { SelfRegistrationToggle } from "../components/SelfRegistrationToggle";
import { useApi } from "../hooks/useApi";
import { serializeRules } from "../lib/access-rules";
import {
  type AdminApp,
  ApiError,
  type RuntimeConfigInput,
  type SalesforceOrg,
  type UpstreamProbeResult,
  api,
} from "../lib/api";

/** Microsoft-Teams-Deeplink für einen 1:1-Chat mit der angegebenen E-Mail (#36). */
function teamsChatUrl(email: string): string {
  return `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(email)}`;
}

/**
 * Owner-Zelle der App-Liste (#36): zeigt den Klartextnamen des App-Erstellers.
 * Mit E-Mail wird der Name zum Microsoft-Teams-Chat-Deeplink; ohne E-Mail bleibt
 * er reiner Text; ohne auflösbaren Owner ein neutraler Platzhalter.
 */
function OwnerCell({
  display,
  email,
}: {
  display: string | null;
  email: string | null;
}): JSX.Element {
  const { t } = useTranslation();
  const label = display ?? email;
  if (!label) {
    return <span className="admin__owner admin__owner--none">{t("admin.owner.unknown")}</span>;
  }
  if (!email) {
    return <span className="admin__owner">{label}</span>;
  }
  return (
    <a
      className="admin__owner"
      href={teamsChatUrl(email)}
      target="_blank"
      rel="noreferrer"
      title={t("admin.owner.teamsHint", { email })}
    >
      {label}
    </a>
  );
}

export interface AdminAppsProps {
  onOpenDetail?: (id: string) => void;
  /** #8: Gast-Share-Checkbox im Anlage-Formular nur im Showroom-Modus zeigen. */
  sharingEnabled?: boolean;
}

export function AdminApps(props: AdminAppsProps = {}): JSX.Element {
  const { t } = useTranslation();
  const [refresh, setRefresh] = useState(0);
  const apps = useApi(() => api.admin.apps(), [refresh]);
  const me = useApi(() => api.me(), []);
  const isAdmin = me.status === "ready" && me.data.is_admin;
  // SF orgs are admin-visible to operators too (read-only listing on the
  // server side); used here for the per-app binding dropdown. If SF is
  // disabled the request returns 404 from the unmounted route — we tolerate
  // that by treating it as an empty list.
  const sfOrgs = useApi(async () => {
    try {
      const res = await api.admin.salesforce.orgs();
      return res.orgs;
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 401))
        return [] as SalesforceOrg[];
      throw e;
    }
  }, []);
  const sfOrgsForForm: SalesforceOrg[] = sfOrgs.status === "ready" ? sfOrgs.data : [];
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<AdminApp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [probes, setProbes] = useState<Record<string, UpstreamProbeResult>>({});

  const reload = (): void => setRefresh((r) => r + 1);

  if (apps.status === "loading") return <p className="launchpad__hint">{t("admin.loading")}</p>;
  if (apps.status === "error") {
    const msg = apps.error instanceof ApiError ? apps.error.message : apps.error.message;
    return <p className="launchpad__hint launchpad__hint--error">{msg}</p>;
  }

  const onCreate = async (form: FormState): Promise<void> => {
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        slug: form.slug,
        name: form.name,
        description: form.description || undefined,
        app_type: form.app_type,
        category: form.category || undefined,
        visibility: form.visibility,
        shareable: form.shareable && !form.salesforce_required,
        access_rules: serializeRules(form.access_rules),
      };
      if (form.app_type === "external") {
        payload.target_url = form.target_url;
      } else if (form.app_type === "managed") {
        payload.strip_prefix = form.strip_prefix;
      } else {
        payload.upstream_url = form.upstream_url;
        payload.strip_prefix = form.strip_prefix;
      }
      if (form.app_type !== "external") {
        payload.proxy_settings = {
          salesforce_required: form.salesforce_required,
          salesforce_org_alias: form.salesforce_required ? form.salesforce_org_alias : null,
        };
      }
      const created = await api.admin.create(payload);
      if (form.app_type === "managed") {
        const portRaw = form.internal_port.trim();
        const port = portRaw ? Number.parseInt(portRaw, 10) : Number.NaN;
        const cfg: RuntimeConfigInput = {
          image_ref: form.image_ref.trim(),
        };
        if (Number.isFinite(port)) cfg.internal_port = port;
        await api.admin.runtime.configure(created.id, cfg);
      }
      setCreating(false);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const onPatch = async (id: string, patch: Record<string, unknown>): Promise<void> => {
    setError(null);
    try {
      await api.admin.update(id, patch);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const onDelete = async (id: string, keepVolumes: boolean): Promise<void> => {
    setError(null);
    try {
      await api.admin.delete(id, keepVolumes);
      setDeleting(null);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const onApprove = async (id: string): Promise<void> => {
    setError(null);
    try {
      await api.admin.approve(id);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const onTest = async (id: string): Promise<void> => {
    setError(null);
    try {
      const result = await api.admin.test(id);
      setProbes((prev) => ({ ...prev, [id]: result }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <section className="admin">
      <div className="admin__header">
        <h2>{t("admin.appsTitle")}</h2>
        <button type="button" className="primary" onClick={() => setCreating(true)}>
          {t("admin.newApp")}
        </button>
      </div>
      {isAdmin ? <SelfRegistrationToggle /> : null}
      {error ? <AdminAlert kind="error">{error}</AdminAlert> : null}
      <table className="admin__table">
        <thead>
          <tr>
            <th>{t("admin.col.slug")}</th>
            <th>{t("admin.col.name")}</th>
            <th>{t("admin.col.appType")}</th>
            <th>{t("admin.col.target")}</th>
            <th>{t("admin.col.owner")}</th>
            <th>{t("admin.col.enabled")}</th>
            <th>{t("admin.col.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {apps.data.apps.map((app) => (
            <tr key={app.id}>
              <td>{app.slug}</td>
              <td>
                {app.name}
                {app.update_available ? (
                  <>
                    {" "}
                    <em className="badge badge--update">{t("admin.runtime.updateBadge")}</em>
                  </>
                ) : null}
                {app.status === "pending" ? (
                  <>
                    {" "}
                    <em className="badge badge--pending">{t("admin.status.pending")}</em>
                  </>
                ) : null}
              </td>
              <td>{t(`admin.appType.${app.app_type}`)}</td>
              <td>
                {app.app_type === "external" ? (
                  <code>{app.target_url}</code>
                ) : app.app_type === "managed" ? (
                  <code>
                    {app.public_path} → {app.upstream_url ?? "(hub-managed)"}
                  </code>
                ) : (
                  <code>
                    {app.public_path} → {app.upstream_url}
                  </code>
                )}
              </td>
              <td>
                <OwnerCell display={app.owner_display ?? null} email={app.owner_email ?? null} />
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={app.enabled}
                  onChange={(e) => void onPatch(app.id, { enabled: e.target.checked })}
                />
              </td>
              <td className="admin__actions">
                <button type="button" onClick={() => props.onOpenDetail?.(app.id)}>
                  {t("admin.detail.open")}
                </button>
                {app.status === "pending" ? (
                  <button type="button" onClick={() => void onApprove(app.id)}>
                    {t("admin.approve")}
                  </button>
                ) : null}
                {app.app_type === "proxy" ? (
                  <button type="button" onClick={() => void onTest(app.id)}>
                    {t("admin.test")}
                  </button>
                ) : null}
                <button type="button" onClick={() => setDeleting(app)}>
                  {t("admin.delete")}
                </button>
                {probes[app.id] ? (
                  <span
                    className={
                      probes[app.id]?.ok
                        ? "admin__probe admin__probe--ok"
                        : "admin__probe admin__probe--err"
                    }
                  >
                    {probes[app.id]?.ok ? "✓" : "✗"}{" "}
                    {probes[app.id]?.status ?? probes[app.id]?.error}
                  </span>
                ) : null}
              </td>
            </tr>
          ))}
          {apps.data.apps.length === 0 ? (
            <tr>
              <td colSpan={7} className="admin__empty">
                {t("admin.noApps")}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      {creating ? (
        <AppForm
          sharingEnabled={props.sharingEnabled ?? false}
          initial={EMPTY_FORM}
          sfOrgs={sfOrgsForForm}
          onCancel={() => setCreating(false)}
          onSubmit={onCreate}
          submitLabel={t("admin.create")}
        />
      ) : null}

      {deleting ? (
        <DeleteAppDialog
          appName={deleting.name}
          isManaged={deleting.app_type === "managed"}
          onCancel={() => setDeleting(null)}
          onConfirm={(keepVolumes) => onDelete(deleting.id, keepVolumes)}
        />
      ) : null}
    </section>
  );
}
